export default function Services() {
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const zones = [
    {
      icon: 'ri-building-4-line',
      title: 'Zone A - Métropoles Premium',
      description: 'Paris Île-de-France, Lyon, Nice, Bordeaux',
      price: '45€',
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-100',
      textColor: 'text-purple-600',
      features: ['Forte densité de demandes', 'Budgets élevés', 'Projets premium', 'Marché dynamique']
    },
    {
      icon: 'ri-community-line',
      title: 'Zone B - Grandes Métropoles',
      description: 'Lille, Marseille, Nantes, Toulouse',
      price: '35€',
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-100',
      textColor: 'text-blue-600',
      features: ['Demandes régulières', 'Budgets moyens-élevés', 'Marché stable', 'Forte concurrence']
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Zone C - Départements',
      description: 'Départements à densité faible',
      price: '25€',
      color: 'from-teal-500 to-teal-600',
      bgColor: 'bg-teal-100',
      textColor: 'text-teal-600',
      features: ['Demandes ciblées', 'Moins de concurrence', 'Proximité clients', 'Marché local']
    }
  ];

  return (
    <section id="services" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-600 px-4 py-2 rounded-full mb-4">
            <i className="ri-map-2-line"></i>
            <span className="font-semibold text-sm">Tarification par Zones</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Choisissez Votre Zone d'Intervention
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Des tarifs adaptés à chaque zone géographique pour maximiser votre rentabilité
          </p>
        </div>

        {/* Zones Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {zones.map((zone, index) => (
            <div 
              key={index}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all border border-gray-100 group hover:-translate-y-2"
            >
              <div className={`w-16 h-16 bg-gradient-to-br ${zone.color} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                <i className={`${zone.icon} text-3xl text-white`}></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">{zone.title}</h3>
              <p className="text-gray-600 mb-6 min-h-[48px]">{zone.description}</p>
              
              <div className="mb-6">
                <div className={`text-3xl font-bold ${zone.textColor} mb-1`}>{zone.price}</div>
                <div className="text-sm text-gray-500">par demande</div>
              </div>

              <ul className="space-y-3 mb-6">
                {zone.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-gray-700">
                    <i className="ri-check-line text-green-500"></i>
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              <button 
                onClick={() => scrollToSection('demandes')}
                className={`w-full bg-gradient-to-r ${zone.color} text-white px-6 py-3 rounded-lg font-semibold hover:opacity-90 transition-all whitespace-nowrap cursor-pointer`}
              >
                Voir les Demandes
              </button>
            </div>
          ))}
        </div>

        {/* Zone Details */}
        <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 border border-gray-200 mb-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Détails des Zones</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <i className="ri-building-4-line text-purple-600 text-xl"></i>
                </div>
                <h4 className="font-bold text-gray-900">Zone A</h4>
              </div>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• Paris et Île-de-France</li>
                <li>• Lyon et agglomération</li>
                <li>• Nice et Côte d'Azur</li>
                <li>• Bordeaux Métropole</li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <i className="ri-community-line text-blue-600 text-xl"></i>
                </div>
                <h4 className="font-bold text-gray-900">Zone B</h4>
              </div>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• Lille Métropole</li>
                <li>• Marseille et environs</li>
                <li>• Nantes Métropole</li>
                <li>• Toulouse et agglomération</li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                  <i className="ri-map-pin-line text-teal-600 text-xl"></i>
                </div>
                <h4 className="font-bold text-gray-900">Zone C</h4>
              </div>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• Tous les autres départements</li>
                <li>• Villes moyennes</li>
                <li>• Zones rurales</li>
                <li>• Marchés locaux</li>
              </ul>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="bg-gradient-to-r from-teal-500 to-blue-600 rounded-2xl p-12 text-center text-white">
          <h3 className="text-3xl font-bold mb-4">Besoin d'un Pack Multi-Zones ?</h3>
          <p className="text-xl mb-8 text-blue-50">
            Contactez-nous pour obtenir une offre groupée et économiser sur plusieurs zones
          </p>
          <button 
            onClick={() => scrollToSection('contact')}
            className="inline-flex items-center gap-2 bg-white text-teal-600 px-8 py-4 rounded-lg font-semibold hover:bg-blue-50 transition-all whitespace-nowrap cursor-pointer"
          >
            <i className="ri-message-3-line"></i>
            Nous Contacter
          </button>
        </div>
      </div>
    </section>
  );
}